import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from "axios";
import { 
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_ENDPOINTS } from '../../../../config/api';

const EditRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: `/roles/edit/${id}` } });
      return;
    }
    fetchRoleAndPermissions();
  }, [id, navigate]);

  const fetchRoleAndPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch role and all permissions as before
      const roleResponse = await axios.get(`${API_ENDPOINTS.ROLES.BASE}/${id}`);
      const roleData = roleResponse.data.data;
      setRole(roleData);
      setRoleName(roleData.name);

      // Build a set of permissions the role currently has
      const rolePerms = new Set(roleData.permissions.map(perm => perm.name));

      // Fetch all permissions
      const permissionsResponse = await axios.get(API_ENDPOINTS.PERMISSIONS.BASE);
      const permissionsData = Array.isArray(permissionsResponse.data) ? permissionsResponse.data : permissionsResponse.data.data || [];

      // Group permissions by base name
      const grouped = {};
      permissionsData.forEach(perm => {
        const [action, base] = perm.name.split('-');
        if (!base || !['add', 'edit', 'delete', 'view'].includes(action)) return; // skip malformed or unknown actions
        if (!grouped[base]) {
          grouped[base] = { id: perm.id, name: base, actions: { add: false, edit: false, delete: false, view: false } };
        }
        grouped[base].actions[action] = rolePerms.has(perm.name);
      });

      setPermissions(Object.values(grouped));
      // Also update selectedPermissions
      setSelectedPermissions(
        Object.fromEntries(
          Object.entries(grouped).map(([base, obj]) => [
            base,
            Object.entries(obj.actions).filter(([_, v]) => v).map(([k]) => k)
          ]).filter(([_, actions]) => actions.length > 0)
        )
      );
    } catch (error) {
      console.error('Error fetching role or permissions:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        navigate('/login', { state: { from: `/roles/edit/${id}` } });
      } else {
        setError(error.response?.data?.message || 'Failed to fetch role or permissions');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (permissionId, isChecked) => {
    setPermissions(prev =>
      prev.map(perm =>
        perm.id === permissionId
          ? { ...perm, actions: { add: isChecked, edit: isChecked, delete: isChecked, view: isChecked } }
          : perm
      )
    );
    const permission = permissions.find(p => p.id === permissionId);
    updateSelectedPermissions(permission.name.includes('-') ? permission.name.split('-')[1] : permission.name, isChecked ? ['add', 'edit', 'delete', 'view'] : []);
  };

  const handleToggle = (permissionId, action, isChecked) => {
    setPermissions(prev =>
      prev.map(perm =>
        perm.id === permissionId
          ? { ...perm, actions: { ...perm.actions, [action]: isChecked } }
          : perm
      )
    );

    const permission = permissions.find(p => p.id === permissionId);
    const permName = permission.name.includes('-') ? permission.name.split('-')[1] : permission.name;
    const updatedActions = Object.entries({ ...permission.actions, [action]: isChecked })
      .filter(([_, value]) => value)
      .map(([key]) => key);
    
    updateSelectedPermissions(permName, updatedActions);
  };

  const updateSelectedPermissions = (permissionName, actions) => {
    setSelectedPermissions(prev => {
      const newPermissions = { ...prev };
      if (actions.length > 0) {
        newPermissions[permissionName] = actions;
      } else {
        delete newPermissions[permissionName];
      }
      return newPermissions;
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login', { state: { from: `/roles/edit/${id}` } });
        return;
      }

      if (!roleName.trim()) {
        setError('Role name is required');
        setLoading(false);
        return;
      }

      console.log("--- Debugging Role Update ---");
      console.log("Role ID to update:", id);
      console.log("New Role Name:", roleName);
      console.log("Selected Permissions (intermediate state):", JSON.stringify(selectedPermissions, null, 2));

      const permissionNames = Object.entries(selectedPermissions)
        .flatMap(([permissionName, actions]) => actions.map(action => `${action}-${permissionName}`));

      const payload = {
        name: roleName,
        permissions: permissionNames
      };
      
      console.log("Final payload being sent to API:", JSON.stringify(payload, null, 2));


      // Update role name and permissions in a single request
      await axios.put(`${API_ENDPOINTS.ROLES.BASE}/${id}`, payload);

      console.log("Role updated successfully!");
      navigate('/roles');
    } catch (error) {
      console.error('Error updating role. Full error object:', error);

      if (error.response) {
        console.error("API Error Response Data:", error.response.data);
        console.error("API Error Response Status:", error.response.status);
        console.error("API Error Response Headers:", error.response.headers);
      } else if (error.request) {
        console.error("API Error Request:", error.request);
      } else {
        console.error('Error Message:', error.message);
      }
      console.error("Request Config:", error.config);
      console.log("--- End of Debugging ---");

      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        navigate('/login', { state: { from: `/roles/edit/${id}` } });
      } else {
        setError(error.response?.data?.message || 'Failed to update role');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/roles')} sx={{ mr: 2 }}>
              Back to Roles
            </Button>
            <Typography variant="h5">Edit Role</Typography>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <TextField
                label="Role Name"
                fullWidth
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                margin="normal"
                required
              />

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Select Permissions
              </Typography>

              <TableContainer>
                <Table A>
                  <TableHead>
                    <TableRow>
                      <TableCell>Permission Name</TableCell>
                      <TableCell>All</TableCell>
                      <TableCell>Add</TableCell>
                      <TableCell>Edit</TableCell>
                      <TableCell>Delete</TableCell>
                      <TableCell>View</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {permissions.map((permission) => (
                      <TableRow key={permission.id}>
                        <TableCell>{permission.name.includes('-') ? permission.name.split('-')[1] : permission.name}</TableCell>
                        <TableCell>
                          <Checkbox
                            checked={Object.values(permission.actions).every(v => v)}
                            onChange={(e) => handleSelectAll(permission.id, e.target.checked)}
                          />
                        </TableCell>
                        {['add', 'edit', 'delete', 'view'].map((action) => (
                          <TableCell key={action}>
                            <Checkbox
                              checked={permission.actions[action]}
                              onChange={(e) => handleToggle(permission.id, action, e.target.checked)}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Update Role'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/roles')}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EditRole;